import React from "react";

import { Icon, Spinner } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { FlexCenter, FlexJustify } from "common/fragments/Flex";
import QueryComponent from "core/api/QueryComponent";
import { Fragment, FragmentProvider, FragmentRenderer, IDataFragment } from "core/components/FragmentProvider";
import Class from "core/util/Class";
import { TableView } from "./Table.view";

/**
 * data loading wrapper component
 *
 * @author chills
 */

export interface IColumnProps<P> {
	params: Partial<P>;
	rowIndex: number;
	colIndex: number;
}

export interface IDataTableColumn<T, P> extends IDataFragment<T & IColumnProps<P>> {
	primary?: boolean;
	renderSkeleton?: FragmentRenderer<{rowIndex?: number, colIndex?: number, primary?: boolean}>; // override for the field in fragment, provided more info
}

interface IDataTableProps<D, P extends { sort: string; }, T = D> {
	data?: D[]; // if data is populated table does not do query
	entity: Class<D>;
	params: P;
	defaultParams?: Partial<P>;
	columns: Array<IDataTableColumn<T, P>>;
	getColumnProps?: (dataItem: D) => T; // used for passing things other than the elements of the data list to the column fragments
	getId: (dataItem: D) => string;
	renderExpand?: (props: T) => JSX.Element;
	empty?: JSX.Element | string;
	viewKeyPrefix: string;
	setSort?: (sort: string) => void;
}

interface IDataTableState {
	expanded: { [id: string]: boolean; };
}

export default class DataTable<D, T, P extends { sort: string; }> extends React.Component<IDataTableProps<D, P, T>> {

	private renderTable = (data: D[], params: Partial<P>) => {
		const {
			columns = [],
			getId,
			renderExpand,
			viewKeyPrefix,
			getColumnProps,
			setSort
		} = this.props;

		return (
			<div id="tableData">
				<TableView
					columns={columns.map((col) => ({
						...col, // inherit renderers, sort, primary, etc.
						header: (
							<FlexJustify>
								{FragmentProvider.createElement(viewKeyPrefix, col.label)}
								{/*<Icon icon={IconNames.CHEVRON_DOWN} />*/}
							</FlexJustify>
						),
						component: (props: any) => {
							const {rowIndex, colIndex} = props;

							// maybe use a new non-registering provider instead for better encapsulation?
							// this is hoc like react components pretty much
							return FragmentProvider.createElement<IColumnProps<P>>(viewKeyPrefix, col.component, {...props, rowIndex, colIndex});
						}
					}))}
					setSort={setSort}
					data={data}
					getId={getId}
					empty={
						<FlexCenter>
							<Spinner />
						</FlexCenter>
					}
					renderExpand={renderExpand}
					viewKey={viewKeyPrefix + '.table'}
					getColumnProps={getColumnProps}
				/>
			</div>
		);
	}

	public render() {
		const { data, entity } = this.props;

		const params: Partial<P> = this.props.params || {};

		if(Array.isArray(data)) {
			return this.renderTable(data, params);
		}

		return (
			<QueryComponent entity={entity} args={params} type="many">
				{(r) => this.renderTable(r.data, params)}
			</QueryComponent>
		);
	}
}



// WEBPACK FOOTER //
// ./src/common/views/table/DataTable.tsx